import { useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Home } from './routes/Home';
import { Profile } from './routes/Profile';
import { You } from './routes/You';
import { Settings } from './routes/Settings';
import { AuditLog } from './routes/AuditLog';
import { Recover } from './routes/Recover';
import { Wallet } from './routes/Wallet';
import { MissionDetail } from './routes/MissionDetail';
import { MissionEvidence } from './routes/MissionEvidence';
import { Triage } from './routes/Triage';
import { OAuthCallback } from './routes/OAuthCallback';
import OAuthGmailCallback from './routes/OAuthGmailCallback';
import OAuthOutlookCallback from './routes/OAuthOutlookCallback';
import { RawData } from './routes/RawData';
import { ABTesting } from './routes/ABTesting';
import { Results } from './routes/Results';
import { OrbitalDemo } from './routes/OrbitalDemo';
import { Welcome } from './routes/Welcome';
import { Connections } from './routes/Connections';
import { SourceDetail } from './routes/SourceDetail';
import { ReAuth } from './routes/ReAuth';
import { Basket } from './routes/Basket';
import { Inbox } from './routes/Inbox';
import { AuroraShowcase } from './routes/AuroraShowcase';
import { Slice02RuntimeHarness } from './routes/__qa/Slice02RuntimeHarness';
import { AppLayout } from './layouts';
import { AnimatedRoute } from './components/shared/AnimatedRoute';
import { useKeyboardNav } from './hooks/useKeyboardNav';
import { getPlatform } from './utils/platform';
import { SyncStatusIndicator } from './components/SyncStatusIndicator';
import { VersionBadge } from './components/VersionBadge';
import { startTauriOAuth, handleOAuthCallbackFromApp, isOAuthInProgress } from './utils/tauri-oauth';
import { useExtensionCredentialSync } from './hooks/useExtensionCredentialSync';
import { resolveWelcomeOAuthReturn } from './lib/welcome/oauth-return-boundary';

export function App() {
  const slice02QaHarnessEnabled = import.meta.env.VITE_SLICE02_QA_HARNESS === 'true';
  const location = useLocation();
  const isQaRoute = location.pathname.startsWith('/__qa/');

  // Enable keyboard navigation
  useKeyboardNav();
  useExtensionCredentialSync();

  const navigate = useNavigate();

  // Handle deep link URLs (shared handler for both sources)
  const handleDeepLinkUrls = async (urls: string[]) => {
    console.log('[App] Processing deep link URLs:', urls);

    for (const url of urls) {
      // Handle Privy auth callback deep links (ownyou://auth/callback?...)
      // Privy social login in Tauri opens system browser, which redirects back via deep link.
      // Convert to in-app navigation so PrivyProvider can process the auth params.
      if (url.startsWith('ownyou://auth/callback')) {
        console.log('[App] Privy auth callback deep link received');
        try {
          const urlObj = new URL(url);
          navigate(`/auth/callback${urlObj.search}`);
        } catch (err) {
          console.error('[App] Failed to process Privy auth callback:', err);
        }
        continue;
      }

      // Handle OAuth callbacks - this is a FALLBACK handler
      // The primary handler is in startTauriOAuth() which has its own onOpenUrl listener
      // Only process here if OAuth is NOT already being handled by startTauriOAuth
      if (url.startsWith('ownyou://oauth/callback')) {
        if (isOAuthInProgress()) {
          console.log('[App] OAuth callback received but startTauriOAuth is handling it, skipping');
          continue;
        }
        console.log('[App] OAuth callback received (fallback), processing via handleOAuthCallbackFromApp');
        try {
          const tokenData = await handleOAuthCallbackFromApp(url);
          if (tokenData) {
            // Parse the state to get the source ID
            const urlObj = new URL(url);
            const sourceId = urlObj.searchParams.get('state') as 'outlook' | 'gmail' | null;
            if (sourceId) {
              console.log('[App] OAuth callback successful for:', sourceId);
              // Store serialized token data for Settings to pick up
              sessionStorage.setItem('oauth_token_data', JSON.stringify(tokenData));
              sessionStorage.setItem('oauth_provider', sourceId);
              const welcomeReturn = resolveWelcomeOAuthReturn({
                state: sourceId,
                sourceId,
                status: 'success',
              });
              // Welcome-started OAuth resumes Welcome; all other deep links keep the existing Settings pickup.
              navigate(welcomeReturn.handled && welcomeReturn.nextRoute
                ? welcomeReturn.nextRoute
                : '/settings?tab=data');
            }
          }
        } catch (err) {
          console.error('[App] OAuth callback processing failed:', err);
        }
        continue; // Don't process this URL further
      }

      // Handle connect requests from PWA
      if (url.startsWith('ownyou://connect/')) {
        const provider = url.replace('ownyou://connect/', '').split('?')[0];
        console.log('[App] Connect request for:', provider);

        // Navigate to settings and trigger OAuth
        navigate('/settings');

        // Small delay to ensure Settings component is mounted
        setTimeout(async () => {
          if (provider === 'outlook' || provider === 'gmail') {
            try {
              const tokenData = await startTauriOAuth(provider);
              if (tokenData) {
                console.log('[App] OAuth successful for:', provider);
                // Store serialized token data for Settings to pick up
                // Settings.tsx has a useEffect that checks for these and calls connectSource()
                sessionStorage.setItem('oauth_token_data', JSON.stringify(tokenData));
                sessionStorage.setItem('oauth_provider', provider);
                // Navigate to settings data tab to trigger token pickup and show confirmation
                navigate('/settings?tab=data');
              }
            } catch (err) {
              console.error('[App] OAuth failed:', err);
            }
          }
        }, 500);
      }
    }
  };

  // Handle deep links from PWA (ownyou://connect/outlook)
  useEffect(() => {
    if (getPlatform() !== 'tauri') return;

    const setupDeepLinkHandler = async () => {
      try {
        // Import deep-link plugin functions
        const { onOpenUrl, getCurrent } = await import('@tauri-apps/plugin-deep-link');

        // CRITICAL: Check for deep links that LAUNCHED the app (cold start)
        // This handles the case where the app was started via ownyou:// URL
        // and the URL needs to be processed before onOpenUrl listener is active
        const initialUrls = await getCurrent();
        if (initialUrls && initialUrls.length > 0) {
          console.log('[App] Cold-start deep link detected:', initialUrls);
          handleDeepLinkUrls(initialUrls);
        }

        // Listen for deep links received while app is running
        const unlistenDeepLink = await onOpenUrl((urls) => {
          console.log('[App] Deep link received (deep-link plugin):', urls);
          handleDeepLinkUrls(urls);
        });

        // Also listen for deep links via the single-instance plugin (when another instance is blocked)
        const { listen } = await import('@tauri-apps/api/event');
        const unlistenSingleInstance = await listen<string[]>('deep-link-received', (event) => {
          console.log('[App] Deep link received (single-instance plugin):', event.payload);
          handleDeepLinkUrls(event.payload);
        });

        return () => {
          unlistenDeepLink();
          unlistenSingleInstance();
        };
      } catch (err) {
        console.warn('[App] Deep link setup failed (expected in PWA):', err);
      }
    };

    setupDeepLinkHandler();
  }, [navigate]);

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<AnimatedRoute><Home /></AnimatedRoute>} />
        <Route path="/you" element={<AnimatedRoute><You /></AnimatedRoute>} />
        <Route path="/you/timeline" element={<AnimatedRoute><You /></AnimatedRoute>} />
        <Route path="/you/settings" element={<AnimatedRoute><Settings /></AnimatedRoute>} />
        <Route path="/profile" element={<AnimatedRoute><Profile /></AnimatedRoute>} />
        <Route path="/settings" element={<AnimatedRoute><Settings /></AnimatedRoute>} />
        <Route path="/you/settings/audit" element={<AnimatedRoute><AuditLog /></AnimatedRoute>} />
        <Route path="/recover" element={<AnimatedRoute><Recover /></AnimatedRoute>} />
        <Route path="/welcome" element={<AnimatedRoute><Welcome /></AnimatedRoute>} />
        <Route path="/onboarding" element={<AnimatedRoute><Welcome entryRoute="onboarding" /></AnimatedRoute>} />
        <Route path="/ikigai" element={<AnimatedRoute><You /></AnimatedRoute>} />
        <Route path="/inbox" element={<AnimatedRoute><Inbox /></AnimatedRoute>} />
        <Route path="/wallet" element={<AnimatedRoute><Wallet /></AnimatedRoute>} />
        <Route path="/mission/:id" element={<AnimatedRoute><MissionDetail /></AnimatedRoute>} />
        <Route path="/mission/:id/evidence" element={<AnimatedRoute><MissionEvidence /></AnimatedRoute>} />
        <Route path="/triage" element={<AnimatedRoute><Triage /></AnimatedRoute>} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route path="/oauth/gmail/callback" element={<OAuthGmailCallback />} />
        <Route path="/oauth/outlook/callback" element={<OAuthOutlookCallback />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />
        <Route path="/data/:sourceType" element={<RawData />} />
        <Route path="/data" element={<RawData />} />
        <Route path="/ab-testing" element={<ABTesting />} />
        <Route path="/results" element={<Results />} />
        <Route path="/orbital-demo" element={<OrbitalDemo />} />
        <Route path="/connections" element={<AnimatedRoute><Connections /></AnimatedRoute>} />
        <Route path="/connections/:id/reauth" element={<AnimatedRoute><ReAuth /></AnimatedRoute>} />
        <Route path="/connections/:id" element={<AnimatedRoute><SourceDetail /></AnimatedRoute>} />
        <Route path="/basket" element={<AnimatedRoute><Basket /></AnimatedRoute>} />
        <Route path="/dev/aurora" element={<AnimatedRoute><AuroraShowcase /></AnimatedRoute>} />
        {slice02QaHarnessEnabled && (
          <Route path="/__qa/slice-02" element={<AnimatedRoute><Slice02RuntimeHarness /></AnimatedRoute>} />
        )}
      </Routes>

      {!isQaRoute && (
        <>
          {/* Sync Status Indicator - Sprint 11b Bugfix 14 */}
          <SyncStatusIndicator />

          {/* Global Version Badge - shows on ALL non-QA pages at top */}
          <VersionBadge className="fixed top-1 right-2 z-50" />
        </>
      )}
    </AppLayout>
  );
}
